import React, { useState } from 'react';
import { Grid, Button, TextField } from '@mui/material';
import { useQueryClient } from 'react-query';
import { schedulerService } from '../../../../core/services/schedulerService';
import { GeneralSettings, GeneralSettingsRequest } from '../../../../types/scheduler';
import styles from '../SchedulerSetting.module.scss';
import { isSchedulerSettingSet } from './schedulerSettingsDisplay';

interface EditGeneralSettingsProps {
	settings: GeneralSettings | null;
	handleBackClick?: () => void;
	onSettingsUpdated?: () => void;
}

type NumericSettingKey =
	| 'ageOfMinor'
	| 'overtimeDailyHoursAt'
	| 'overtimeDailyWarningAt'
	| 'overtimeWeeklyHoursAt'
	| 'timeIncrement'
	| 'overtimeWeeklyWarningAt'
	| 'defaultShiftLengthHours'
	| 'minShiftLength'
	| 'maxShiftLength';

type SettingsFormValues = {
	workplace: string | null;
} & Record<NumericSettingKey, number | ''>;

const toFormValue = (value: number | null | undefined): number | '' =>
	isSchedulerSettingSet(value) ? value : '';

const EditGeneralSettings: React.FC<EditGeneralSettingsProps> = ({
	settings,
	handleBackClick,
	onSettingsUpdated,
}) => {
	const queryClient = useQueryClient();
	const [settingsBuffer, setSettingsBuffer] = useState<SettingsFormValues>(() => {
		const workplaceId = localStorage.getItem('workplace-id');
		const workplace =
			settings?.workplace ?? (workplaceId ? `/api/workplaces/${workplaceId}` : null);

		return {
			workplace,
			ageOfMinor: toFormValue(settings?.ageOfMinor),
			overtimeDailyHoursAt: toFormValue(settings?.overtimeDailyHoursAt),
			overtimeDailyWarningAt: toFormValue(settings?.overtimeDailyWarningAt),
			overtimeWeeklyHoursAt: toFormValue(settings?.overtimeWeeklyHoursAt),
			timeIncrement: toFormValue(settings?.timeIncrement),
			overtimeWeeklyWarningAt: toFormValue(settings?.overtimeWeeklyWarningAt),
			defaultShiftLengthHours: toFormValue(settings?.defaultShiftLengthHours),
			minShiftLength: toFormValue(settings?.minShiftLength),
			maxShiftLength: toFormValue(settings?.maxShiftLength),
		};
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const updateNumericField = (field: NumericSettingKey, rawValue: string) => {
		setSettingsBuffer({
			...settingsBuffer,
			[field]: rawValue === '' ? '' : Number(rawValue),
		});
	};

	const requireNumericField = (
		value: number | '',
		label: string
	): value is number => {
		if (value === '') {
			setError(`${label} is required`);
			return false;
		}
		return true;
	};

	const validateSettings = (): GeneralSettingsRequest | null => {
		if (!requireNumericField(settingsBuffer.ageOfMinor, 'Age of minor')) return null;
		if (!requireNumericField(settingsBuffer.overtimeDailyHoursAt, 'Overtime daily hours')) return null;
		if (!requireNumericField(settingsBuffer.overtimeDailyWarningAt, 'Overtime daily warning')) return null;
		if (!requireNumericField(settingsBuffer.overtimeWeeklyHoursAt, 'Overtime weekly hours')) return null;
		if (!requireNumericField(settingsBuffer.overtimeWeeklyWarningAt, 'Overtime weekly warning')) return null;
		if (!requireNumericField(settingsBuffer.timeIncrement, 'Time increment')) return null;
		if (!requireNumericField(settingsBuffer.defaultShiftLengthHours, 'Default shift length')) return null;
		if (!requireNumericField(settingsBuffer.minShiftLength, 'Minimum shift length')) return null;
		if (!requireNumericField(settingsBuffer.maxShiftLength, 'Maximum shift length')) return null;

		const {
			workplace,
			ageOfMinor,
			overtimeDailyHoursAt,
			overtimeDailyWarningAt,
			overtimeWeeklyHoursAt,
			timeIncrement,
			overtimeWeeklyWarningAt,
			defaultShiftLengthHours,
			minShiftLength,
			maxShiftLength,
		} = settingsBuffer;

		if (ageOfMinor < 0 || ageOfMinor > 120) {
			setError('Age of minor must be between 0 and 120');
			return null;
		}

		if (overtimeDailyHoursAt <= 0 || overtimeDailyHoursAt > 24) {
			setError('Overtime daily hours must be between 1 and 24');
			return null;
		}

		if (overtimeDailyWarningAt < 0 || overtimeDailyWarningAt >= overtimeDailyHoursAt) {
			setError('Overtime daily warning must be less than overtime daily hours');
			return null;
		}

		if (overtimeWeeklyHoursAt <= 0 || overtimeWeeklyHoursAt > 168) {
			setError('Overtime weekly hours must be between 1 and 168');
			return null;
		}

		if (overtimeWeeklyWarningAt < 0 || overtimeWeeklyWarningAt >= overtimeWeeklyHoursAt) {
			setError('Overtime weekly warning must be less than overtime weekly hours');
			return null;
		}

		if (timeIncrement <= 0 || timeIncrement > 60) {
			setError('Time increment must be between 1 and 60 minutes');
			return null;
		}

		if (defaultShiftLengthHours <= 0 || defaultShiftLengthHours > 24) {
			setError('Default shift length must be between 1 and 24 hours');
			return null;
		}

		if (minShiftLength <= 0 || minShiftLength > 24) {
			setError('Minimum shift length must be between 1 and 24 hours');
			return null;
		}

		if (maxShiftLength <= 0 || maxShiftLength > 24) {
			setError('Maximum shift length must be between 1 and 24 hours');
			return null;
		}

		if (minShiftLength >= maxShiftLength) {
			setError('Minimum shift length must be less than maximum shift length');
			return null;
		}

		return {
			workplace,
			ageOfMinor,
			overtimeDailyHoursAt,
			overtimeDailyWarningAt,
			overtimeWeeklyHoursAt,
			timeIncrement,
			overtimeWeeklyWarningAt,
			defaultShiftLengthHours,
			minShiftLength,
			maxShiftLength,
		};
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		const validatedSettings = validateSettings();
		if (!validatedSettings) {
			return;
		}

		try {
			setIsSubmitting(true);
			await schedulerService.saveSettings(validatedSettings, settings);

			queryClient.invalidateQueries('generalSettings');

			onSettingsUpdated?.();
			setTimeout(() => {
				handleBackClick?.();
			}, 500);
		} catch (err: any) {
			setError(err?.message || 'Failed to update settings');
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.editGeneralSettings}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={handleBackClick} variant="contained">
						Back
					</Button>
					<Button
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isSubmitting}
						variant="contained"
					>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Edit General Settings</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<TextField
					label="Minor if Less Than"
					name="ageOfMinor"
					type="number"
					value={settingsBuffer.ageOfMinor}
					onChange={(e) => updateNumericField('ageOfMinor', e.target.value)}
					placeholder="Enter age of minor"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 0, max: 120 }}
				/>

				<TextField
					label="Overtime Daily Hours At"
					name="overtimeDailyHoursAt"
					type="number"
					value={settingsBuffer.overtimeDailyHoursAt}
					onChange={(e) => updateNumericField('overtimeDailyHoursAt', e.target.value)}
					placeholder="Enter hours"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 24 }}
				/>

				<TextField
					label="Overtime Daily Warning At"
					name="overtimeDailyWarningAt"
					type="number"
					value={settingsBuffer.overtimeDailyWarningAt}
					onChange={(e) => updateNumericField('overtimeDailyWarningAt', e.target.value)}
					placeholder="Enter warning hours"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 0 }}
				/>

				<TextField
					label="Overtime Weekly Hours At"
					name="overtimeWeeklyHoursAt"
					type="number"
					value={settingsBuffer.overtimeWeeklyHoursAt}
					onChange={(e) => updateNumericField('overtimeWeeklyHoursAt', e.target.value)}
					placeholder="Enter hours"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 168 }}
				/>

				<TextField
					label="Overtime Weekly Warning At"
					name="overtimeWeeklyWarningAt"
					type="number"
					value={settingsBuffer.overtimeWeeklyWarningAt}
					onChange={(e) => updateNumericField('overtimeWeeklyWarningAt', e.target.value)}
					placeholder="Enter warning hours"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 0 }}
				/>

				<TextField
					label="Time Increment (minutes)"
					name="timeIncrement"
					type="number"
					value={settingsBuffer.timeIncrement}
					onChange={(e) => updateNumericField('timeIncrement', e.target.value)}
					placeholder="Enter time increment in minutes"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 60 }}
				/>

				<TextField
					label="Default Shift Length (hours)"
					name="defaultShiftLengthHours"
					type="number"
					value={settingsBuffer.defaultShiftLengthHours}
					onChange={(e) => updateNumericField('defaultShiftLengthHours', e.target.value)}
					placeholder="Enter default shift length"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 24 }}
				/>

				<TextField
					label="Minimum Shift Length (hours)"
					name="minShiftLength"
					type="number"
					value={settingsBuffer.minShiftLength}
					onChange={(e) => updateNumericField('minShiftLength', e.target.value)}
					placeholder="Enter minimum shift length"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 24 }}
				/>

				<TextField
					label="Maximum Shift Length (hours)"
					name="maxShiftLength"
					type="number"
					value={settingsBuffer.maxShiftLength}
					onChange={(e) => updateNumericField('maxShiftLength', e.target.value)}
					placeholder="Enter maximum shift length"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					inputProps={{ min: 1, max: 24 }}
				/>
			</form>
		</div>
	);
};

export default EditGeneralSettings;
